import React, { useEffect } from "react";
import GaugeComponent from "react-gauge-component";

interface DeviceProps {
	device: {
		tempSet: number;
		tempVal: number;
		tempDiff: number;
	};
	myGraph: boolean;
	page: string;
}

const Gauge: React.FC<DeviceProps> = ({ device, myGraph, page }) => {
	useEffect(() => {
		const observer = new ResizeObserver(() => {
			requestAnimationFrame(() => {
				// resize observer kept intentionally
			});
		});

		observer.observe(document.body);
		return () => observer.disconnect();
	}, []);

	const tempSet = Number(device?.tempSet) || 0;
	const tempVal = Number(device?.tempVal) || 0;
	const tempDiff = Math.abs(Number(device?.tempDiff)) || 0;

	// Prevent invalid gauge ranges
	const minValue = tempSet - tempDiff * 2;
	const maxValue = tempSet + tempDiff * 2;

	// SubArc limits MUST:
	// minValue < lowLimit < highLimit < maxValue
	const lowLimit = Math.max(minValue, tempSet - tempDiff);
	const highLimit = Math.min(maxValue, tempSet + tempDiff);

	// Hard guard: react-gauge-component WILL crash without this
	if (
		!isFinite(minValue) ||
		!isFinite(maxValue) ||
		lowLimit >= highLimit ||
		tempDiff === 0
	) {
		return null; // or a placeholder / skeleton
	}

	return (
		<GaugeComponent
			type="semicircle"
			marginInPercent={
				page === "devicePage"
					? { top: 0.05, bottom: 0.07, left: 0.025, right: 0.025 }
					: { top: 0.05, bottom: 0, left: 0.025, right: 0.025 }
			}
			style={{
				width: "100%",
				marginBottom: "-25px",
				marginTop: "-10px",
			}}
			value={tempVal}
			minValue={minValue}
			maxValue={maxValue}
			arc={{
				width: 0.1,
				padding: 0.005,
				cornerRadius: 1,
				subArcs: [
					{
						limit: lowLimit,
						color: "#264b96",
						showTick: false,
					},
					{
						limit: highLimit,
						color: "#27b376",
						showTick: false,
					},
					{
						color: "#bf212f",
					},
				],
			}}
			pointer={{
				color: "rgba(91, 91, 91, .54)",
				length: 1.05,
				width: 10,
				elastic: true,
			}}
			labels={{
				valueLabel: {
					formatTextValue: (value) => `${value}`,
					matchColorWithArc: true,
					style: {
						display: "none",
					},
				},
				tickLabels: {
					type: "inner",
					defaultTickValueConfig: {
						formatTextValue: (value: number) => `${value}`,
						style:
							page !== "devicePage"
								? { fontSize: 9, textShadow: "none", fill: "black" }
								: { fontSize: 14, textShadow: "none", fill: "black" },
					},
					defaultTickLineConfig: {
						hide: true,
					},
					ticks: [
						{ value: lowLimit },
						{
							value: tempSet,
							lineConfig: {
								color: "black",
								hide: false,
								distanceFromArc: -1.8,
							},
						},
						{ value: highLimit },
					],
				},
			}}
		/>
	);
};

export default Gauge;
